import { Offcanvas, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import moment from "moment";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  from_year: string;
  to_year: string;
  name: string;
  designation: string;
  company_name: string;
};

const AddPastPresident = ({ show, handleClose, data }: EventFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>();
  const [value, setValue] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        from_year: data.from_year,
        to_year: data.to_year,
        company_name: data.company_name,
        designation: data.designation,
      });
      setValue(data.description);
    } else {
      reset({});
      setValue("");
    }
  }, [show]);

  const onSubmit = (request: FormData) => {
    const formData = new FormData();
    formData.append("name", request.name);
    formData.append("designation", request.designation);
    formData.append("company_name", request.company_name);
    formData.append("from_year", request.from_year);
    formData.append("to_year", request.to_year);
    if (file) {
      formData.append("image", file);
    }
    setLoading(true);
    if (data?._id) {
      WebService.putAPI({
        action: `past-president/edit-past-president/${data._id}`,
        body: request,
      })
        .then((res: any) => {
          toast.success(res.message);
          setLoading(false);
          if (file) {
            uploadBanner(res?.data?._id);
          }
          handleClose();
          reset();
          setValue("");
          setFile(null);
        })
        .catch((error: any) => {
          setLoading(false);
          toast.error(error.response?.data?.message || "An error occurred");
        });
    } else {
      WebService.fileUploadAPI({
        action: `past-president/add-past-president`,
        body: formData,
      })
        .then((res: any) => {
          toast.success(res.message);
          reset();
          handleClose();
          setFile(null);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          toast.error(error.response?.data?.message || "An error occurred");
        });
    }
  };

  const uploadBanner = (id: string) => {
    const formData = new FormData();
    if (file) {
      formData.append("banner", file);
    }
    WebService.fileUploadAPI({
      action: `events/banner/${id}`,
      body: formData,
    })
      .then((res: any) => {})
      .catch((error: any) => {
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          reset();
          setFile(null);
          handleClose();
        }}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {data ? "Edit Past President" : "Add Past President"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Form.Group className="mb-2" controlId="eventImage">
              <Form.Label className="label">Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                className="custom-image-upload"
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  const file = input.files && input.files[0];
                  if (file) {
                    setFile(file);
                  }
                }}
              />
            </Form.Group>
            {(file || data?.image) && (
              <div className="mb-2 event-image">
                <img
                  src={file ? URL.createObjectURL(file) : data?.image}
                  alt="Event"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <div className="error-message">{errors.name.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventLocation">
              <Form.Label className="label">
                Designation <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter designation"
                {...register("designation", {
                  required: "Designation is required",
                })}
              />
              {errors.designation && (
                <div className="error-message">
                  {errors.designation.message}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventCompany">
              <Form.Label className="label">
                Company Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter company name"
                {...register("company_name", {
                  required: "Company name is required",
                })}
              />
              {errors.company_name && (
                <div className="error-message">
                  {errors.company_name.message}
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventYear">
              <Form.Label className="label">
                From Year <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                {...register("from_year", { required: "Year is required" })}
                defaultValue=""
              >
                <option value="" disabled>
                  Select year
                </option>
                {Array.from({ length: 30 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </Form.Select>
              {errors.from_year && (
                <div className="error-message">{errors.from_year.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventYear">
              <Form.Label className="label">
                To Year <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                {...register("to_year", {
                  required: "Year is required",
                  validate: (value) => {
                    const fromYear = watch("from_year"); // 👈 get from_year value
                    if (fromYear && parseInt(value) < parseInt(fromYear)) {
                      return "To Year must be greater than or equal to From Year";
                    }
                    return true;
                  },
                })}
                defaultValue=""
              >
                <option value="" disabled>
                  Select year
                </option>
                {Array.from({ length: 30 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  const fromYear = watch("from_year");
                  const disabled = fromYear ? year < parseInt(fromYear) : false; // 👈 disable years < from_year
                  return (
                    <option key={year} value={year} disabled={disabled}>
                      {year}
                    </option>
                  );
                })}
              </Form.Select>
              {errors.to_year && (
                <div className="error-message">{errors.to_year.message}</div>
              )}
            </Form.Group>
          </Form>
        </Offcanvas.Body>
        <div className="d-flex justify-content-end p-3">
          <CustomButton
            isLoading={isLoading}
            label="Save"
            className="w-100"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </Offcanvas>
    </>
  );
};

export default AddPastPresident;
